import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Menu,
  ListSubheader,
  MenuItem,
  Popover,
  Tooltip,
  useTheme,
  Typography,
} from '@mui/material';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PersonIcon from '@mui/icons-material/Person';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { camelCaseToTitleCase } from '../utils/stringConversion';

// Define types for appointment menu items
interface SectionMenuItem {
  field: string;
  label: string;
}

interface MenuSection {
  header: string;
  items: SectionMenuItem[];
}

interface MessageEditorProps {
  message: string;
  onChange: (message: string) => void;
  smsVariables: any[];
}

export default function MessageEditor({
  message,
  onChange,
  smsVariables,
}: MessageEditorProps) {
  const theme = useTheme();

  // State for deal menu
  const [dealAnchorEl, setDealAnchorEl] = useState<null | HTMLElement>(null);
  const dealMenuOpen = Boolean(dealAnchorEl);

  // State for time menu
  const [timeAnchorEl, setTimeAnchorEl] = useState<null | HTMLElement>(null);
  const timeMenuOpen = Boolean(timeAnchorEl);

  // State for person menu
  const [personAnchorEl, setPersonAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const personMenuOpen = Boolean(personAnchorEl);

  // State for emoji picker
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
  const emojiMenuOpen = Boolean(emojiAnchorEl);

  // For cursor position tracking
  const [textFieldRef, setTextFieldRef] = useState<HTMLInputElement | null>(
    null
  );

  // Track cursor position changes for better consistency
  useEffect(() => {
    if (textFieldRef) {
      const trackCursorPosition = () => {
        // We're just tracking the cursor position changes,
        // but not storing it in state to avoid unnecessary re-renders
        // This helps ensure the events are attached properly
      };

      textFieldRef.addEventListener('click', trackCursorPosition);
      textFieldRef.addEventListener('keyup', trackCursorPosition);

      return () => {
        textFieldRef.removeEventListener('click', trackCursorPosition);
        textFieldRef.removeEventListener('keyup', trackCursorPosition);
      };
    }
  }, [textFieldRef]);

  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleButtonClose = (
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => {
    setAnchorEl(null);
  };

  // Core insert function that all insertion methods will use
  const insertTextAtCursor = (
    textToInsert: string,
    closeMenu?: () => void,
    cursorOffsetOverride?: number
  ) => {
    // Get precise cursor position
    const cursorPosition = textFieldRef?.selectionStart ?? 0;

    let updatedMessage;

    // Special handling for cursor at position 0
    if (cursorPosition === 0) {
      updatedMessage = textToInsert + message;
    } else {
      updatedMessage =
        message.substring(0, cursorPosition) +
        textToInsert +
        message.substring(cursorPosition);
    }

    // Update the message
    onChange(updatedMessage);

    // Close menu if provided
    if (closeMenu) {
      closeMenu();
    }

    // Calculate new cursor position
    const newCursorPosition =
      cursorPosition + (cursorOffsetOverride ?? textToInsert.length);

    // Position cursor after the inserted text
    requestAnimationFrame(() => {
      if (textFieldRef) {
        textFieldRef.focus();

        try {
          // Try the most direct method first
          textFieldRef.setSelectionRange(newCursorPosition, newCursorPosition);
        } catch (e) {
          // Fallback to the traditional approach
          setTimeout(() => {
            if (textFieldRef) {
              textFieldRef.selectionStart = newCursorPosition;
              textFieldRef.selectionEnd = newCursorPosition;
            }
          }, 10);
        }
      }
    });
  };

  // Insert variable at cursor position
  const insertField = (
    variableName: string,
    isTimeField: boolean = false,
    isPersonField: boolean = false
  ) => {
    const variableText = `{{${variableName}}}`;

    // Determine which menu to close
    const closeMenuFn = () => {
      if (isTimeField) {
        handleButtonClose(setTimeAnchorEl);
      } else if (isPersonField) {
        handleButtonClose(setPersonAnchorEl);
      } else {
        handleButtonClose(setDealAnchorEl);
      }
    };

    insertTextAtCursor(variableText, closeMenuFn);
  };

  // Update the insertEmoji function to work with emoji-picker-react
  const insertEmoji = (emojiData: EmojiClickData) => {
    insertTextAtCursor(emojiData.emoji, () =>
      handleButtonClose(setEmojiAnchorEl)
    );
  };

  // Create text formatter that handles special formatting
  interface FormattedTextResult {
    text: string;
    cursorOffset: number;
  }

  // Generic function to insert formatted text at cursor position
  const insertFormattedText = (
    formatter: (cursorPosition: number) => FormattedTextResult
  ) => {
    // Get precise cursor position
    const cursorPosition = textFieldRef?.selectionStart ?? 0;

    // Get the formatted text and cursor offset from the formatter function
    const { text, cursorOffset } = formatter(cursorPosition);

    // Insert the text with the cursor offset
    insertTextAtCursor(text, undefined, cursorOffset);
  };

  // Format bullet point text
  const formatBulletPoint = (cursorPosition: number): FormattedTextResult => {
    // Determine if we need a newline before the bullet
    const needsNewline =
      cursorPosition > 0 &&
      message.charAt(cursorPosition - 1) !== '\n' &&
      cursorPosition !== 0;

    // Create bullet with tab indentation (using spaces)
    const tabIndent = '    '; // 4 spaces for tab indentation
    const bulletText = `${needsNewline ? '\n' : ''}${tabIndent}• `;

    return {
      text: bulletText,
      cursorOffset: bulletText.length,
    };
  };

  // Format numbered list item
  const formatNumberedItem = (cursorPosition: number): FormattedTextResult => {
    // Determine if we need a newline before the number
    const needsNewline =
      cursorPosition > 0 &&
      message.charAt(cursorPosition - 1) !== '\n' &&
      cursorPosition !== 0;

    // Count existing numbered bullets and increment appropriately
    let nextNumber = 1;

    // Check if we're continuing an existing numbered list
    if (message.includes('1. ')) {
      // Find all numbered bullets in the message
      const numberedBullets = message.match(/\d+\.\s/g) || [];
      if (numberedBullets.length > 0) {
        // Extract the numbers and find the maximum
        const numbers = numberedBullets.map((bullet: string) =>
          parseInt(bullet.match(/\d+/)?.[0] || '0', 10)
        );
        nextNumber = Math.max(...numbers) + 1;
      }
    }

    // Create numbered bullet with tab indentation (using spaces)
    const tabIndent = '    '; // 4 spaces for tab indentation
    const numberedText = `${
      needsNewline ? '\n' : ''
    }${tabIndent}${nextNumber}. `;

    return {
      text: numberedText,
      cursorOffset: numberedText.length,
    };
  };

  // Function to insert bullet point at cursor position
  const insertBulletPoint = () => {
    insertFormattedText(formatBulletPoint);
  };

  // Function to insert numbered bullet point at cursor position
  const insertNumberedBullet = () => {
    insertFormattedText(formatNumberedItem);
  };

  // Handle key press in the text field
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    e.preventDefault(); // Prevent default Enter behavior

    // Get precise cursor position
    const currentPosition = textFieldRef?.selectionStart ?? 0;

    // Special handling for cursor at the beginning of the text
    if (currentPosition === 0) {
      // Insert newline at the beginning using our common function
      insertTextAtCursor('\n');
      return;
    }

    // Get the current line text
    const textBeforeCursor = message.substring(0, currentPosition);
    const lastNewlineBeforeCursor = textBeforeCursor.lastIndexOf('\n');
    const currentLineText = textBeforeCursor.substring(
      lastNewlineBeforeCursor + 1
    );

    // Check if the current line has a bullet point or numbered item
    const tabIndent = '    '; // 4 spaces for tab indentation
    const hasBullet = currentLineText.includes(`${tabIndent}• `);
    const hasNumber = /\s{4}\d+\.\s/.test(currentLineText);

    if (!(hasBullet || hasNumber)) {
      // Normal Enter behavior - just insert a newline
      insertTextAtCursor('\n');
    } else {
      // Check if the bullet/number line is empty (just the prefix)
      const isBulletEmpty =
        hasBullet && currentLineText.trim() === `${tabIndent}• `.trim();
      const isNumberEmpty =
        hasNumber && /^\s{4}\d+\.\s*$/.test(currentLineText);

      if (isBulletEmpty || isNumberEmpty) {
        // Remove the empty bullet/number line
        const lineStart = lastNewlineBeforeCursor + 1;
        const updatedMessage =
          message.substring(0, lineStart) + message.substring(currentPosition);

        // We use onChange directly here since we're removing text, not inserting
        onChange(updatedMessage);

        // Position cursor after update
        requestAnimationFrame(() => {
          if (textFieldRef) {
            textFieldRef.focus();
            textFieldRef.selectionStart = lineStart;
            textFieldRef.selectionEnd = lineStart;
          }
        });
      } else {
        // Add a new bullet/number of the same type
        if (hasBullet) {
          // Insert new bullet point with proper indentation
          insertTextAtCursor(`\n${tabIndent}• `);
        } else if (hasNumber) {
          // Extract current number and increment for the new line
          const match = currentLineText.match(/\s{4}(\d+)\.\s/);
          if (match && match[1]) {
            const currentNumber = parseInt(match[1], 10);
            const nextNumber = currentNumber + 1;
            insertTextAtCursor(`\n${tabIndent}${nextNumber}. `);
          }
        }
      }
    }
  };

  const userMenuSections: MenuSection[] = [
    {
      header: 'Setter',
      items: [
        { field: 'setter.name', label: 'Name' },
        { field: 'setter.phoneNumber', label: 'Phone Number' },
        { field: 'setter.email', label: 'Email' },
      ],
    },
    {
      header: 'Closer',
      items: [
        { field: 'closer.name', label: 'Name' },
        { field: 'closer.phoneNumber', label: 'Phone Number' },
        { field: 'closer.email', label: 'Email' },
      ],
    },
  ];

  // Appointment menu data structure
  const appointmentMenuSections: MenuSection[] = [
    {
      header: 'Appointment Details',
      items: [
        {
          field: 'appointment.statusCapitalized',
          label: 'Status (Scheduled)',
        },
      ],
    },
    {
      header: 'Date Fields',
      items: [
        {
          field: 'appointment.dayOfWeek',
          label: 'Day of Week (Friday)',
        },
        {
          field: 'appointment.dateFormatted',
          label: 'Date (May 02, 2025)',
        },
      ],
    },
    {
      header: 'Time Fields',
      items: [
        {
          field: 'appointment.startTime12h',
          label: 'Start Time - 12h (2:30 PM)',
        },
        {
          field: 'appointment.endTime12h',
          label: 'End Time - 12h (5:00 PM)',
        },
        {
          field: 'appointment.timeRange12h',
          label: 'Time Range - 12h (2:30 PM-5:00 PM)',
        },
        {
          field: 'appointment.startTime',
          label: 'Start Time - 24h (14:30)',
        },
        {
          field: 'appointment.endTime',
          label: 'End Time - 24h (17:00)',
        },
        {
          field: 'appointment.timeRange',
          label: 'Time Range - 24h (14:30-17:00)',
        },
      ],
    },
    {
      header: 'Duration Fields',
      items: [
        {
          field: 'appointment.durationM',
          label: 'Duration - Minutes (75 minutes)',
        },
        {
          field: 'appointment.durationH',
          label: 'Duration - Hours (1.25 hours)',
        },
      ],
    },
    {
      header: 'Combined Date & Time',
      items: [
        {
          field: 'appointment.dateAndTime12h',
          label: 'Full Date & Time - 12h',
        },
        {
          field: 'appointment.dateAndTime',
          label: 'Full Date & Time - 24h',
        },
      ],
    },
  ];

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
      }}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={(theme) => ({ fontWeight: 'bold', color: theme.custom?.text })}
        >
          Message
        </Typography>
        <Box>
          <Tooltip title="Insert Person Field">
            <IconButton
              size="small"
              onClick={(event) => handleButtonClick(event, setPersonAnchorEl)}
              aria-controls={personMenuOpen ? 'person-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={personMenuOpen ? 'true' : undefined}
              sx={(theme) => ({ color: theme.custom?.text })}
            >
              <PersonIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Deal Field">
            <IconButton
              size="small"
              onClick={(event) => handleButtonClick(event, setDealAnchorEl)}
              aria-controls={dealMenuOpen ? 'deal-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={dealMenuOpen ? 'true' : undefined}
              sx={(theme) => ({ color: theme.custom?.text })}
            >
              <TextFieldsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Time Field">
            <IconButton
              size="small"
              onClick={(event) => handleButtonClick(event, setTimeAnchorEl)}
              aria-controls={timeMenuOpen ? 'time-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={timeMenuOpen ? 'true' : undefined}
              sx={(theme) => ({ color: theme.custom?.text })}
            >
              <AccessTimeFilledIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Insert Bullet Point">
            <IconButton
              size="small"
              onClick={insertBulletPoint}
              sx={(theme) => ({ color: theme.custom?.text })}
            >
              <FormatListBulletedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Insert Numbered List Item">
            <IconButton
              size="small"
              onClick={insertNumberedBullet}
              sx={(theme) => ({ color: theme.custom?.text })}
            >
              <FormatListNumberedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Insert Emoji">
            <IconButton
              size="small"
              onClick={(event) => handleButtonClick(event, setEmojiAnchorEl)}
              aria-controls={emojiMenuOpen ? 'emoji-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={emojiMenuOpen ? 'true' : undefined}
              sx={(theme) => ({ color: theme.custom?.text })}
            >
              <EmojiEmotionsIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        value={message || ''}
        inputRef={setTextFieldRef}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={(theme) => ({
          '& .MuiInputBase-root': {
            backgroundColor: theme.custom?.background2,
            color: theme.custom?.text,
          },
          '& .MuiInputBase-input': {
            color: theme.custom?.text,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.custom?.stroke,
          },
        })}
      />

      {/* Person Menu */}
      <Menu
        id="person-menu"
        anchorEl={personAnchorEl}
        open={personMenuOpen}
        onClose={() => handleButtonClose(setPersonAnchorEl)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          sx: (theme) => ({
            maxHeight: 400,
            backgroundColor: theme.custom?.background2,
            width: '280px',
            '& .MuiMenuItem-root': {
              color: theme.custom?.text,
            },
            '& .MuiListSubheader-root': {
              backgroundColor: theme.custom?.background3,
              color: theme.custom?.text,
            },
          }),
        }}
      >
        {userMenuSections.flatMap(
          (section: MenuSection, sectionIndex: number) => [
            <ListSubheader key={`person-header-section-${sectionIndex}`}>
              {section.header}
            </ListSubheader>,
            ...section.items.map((item: SectionMenuItem, itemIndex: number) => (
              <MenuItem
                key={`person-item-section-${sectionIndex}-item-${itemIndex}`}
                onClick={() => insertField(item.field, false, true)}
                style={{ paddingLeft: '36px' }}
              >
                {item.label}
              </MenuItem>
            )),
          ]
        )}
      </Menu>

      {/* Deal Menu */}
      <Menu
        id="deal-menu"
        anchorEl={dealAnchorEl}
        open={dealMenuOpen}
        onClose={() => handleButtonClose(setDealAnchorEl)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          sx: (theme) => ({
            maxHeight: 400,
            backgroundColor: theme.custom?.background2,
            width: '280px',
            '& .MuiMenuItem-root': {
              color: theme.custom?.text,
            },
            '& .MuiListSubheader-root': {
              backgroundColor: theme.custom?.background3,
              color: theme.custom?.text,
            },
          }),
        }}
      >
        {/* Contact Fields */}
        {smsVariables.map((variable, varIndex) => {
          if (variable.type === 'Contact') {
            return [
              <ListSubheader key={`deal-header-${variable.id}-${varIndex}`}>
                {variable.heading}
              </ListSubheader>,
              ...Object.keys(variable.contactFields)
                .filter((key) => variable.contactFields[key].show)
                .map((key, fieldIdx) => (
                  <MenuItem
                    key={`deal-var-${variable.id}-${key}-${fieldIdx}`}
                    style={{
                      paddingLeft: '36px',
                    }}
                    onClick={() =>
                      insertField(`deal.${variable.name}.${key}`, false)
                    }
                  >
                    {camelCaseToTitleCase(key)}
                  </MenuItem>
                )),
            ];
          }
          return (
            <MenuItem
              key={`deal-var-${variable.id}-${varIndex}`}
              onClick={() => insertField(`deal.${variable.name}`, false)}
            >
              {variable.label}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Time Menu */}
      <Menu
        id="time-menu"
        anchorEl={timeAnchorEl}
        open={timeMenuOpen}
        onClose={() => handleButtonClose(setTimeAnchorEl)}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          sx: (theme) => ({
            maxHeight: 400,
            backgroundColor: theme.custom?.background2,
            width: '280px',
            '& .MuiMenuItem-root': {
              color: theme.custom?.text,
            },
            '& .MuiListSubheader-root': {
              backgroundColor: theme.custom?.background3,
              color: theme.custom?.text,
            },
          }),
        }}
      >
        {appointmentMenuSections.flatMap(
          (section: MenuSection, sectionIndex: number) => [
            <ListSubheader key={`time-header-section-${sectionIndex}`}>
              {section.header}
            </ListSubheader>,
            ...section.items.map((item: SectionMenuItem, itemIndex: number) => (
              <MenuItem
                key={`time-item-section-${sectionIndex}-item-${itemIndex}`}
                onClick={() => insertField(item.field, true)}
                style={{ paddingLeft: '36px' }}
              >
                {item.label}
              </MenuItem>
            )),
          ]
        )}
      </Menu>

      {/* Emoji Picker Popover */}
      <Popover
        id="emoji-menu"
        anchorEl={emojiAnchorEl}
        open={emojiMenuOpen}
        onClose={() => handleButtonClose(setEmojiAnchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <EmojiPicker
          onEmojiClick={insertEmoji}
          searchPlaceholder="Search emojis..."
          width={320}
          height={400}
          theme={theme.custom?.isDark ? Theme.DARK : Theme.LIGHT}
          lazyLoadEmojis={true}
        />
      </Popover>
    </Box>
  );
}
